import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  PackConfigGroup,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { groupBy } from '@sorare/core/src/lib/arrays';

import { DailyDeals } from 'components/commonCard/BuyPacks/DailyDeals';

import type { CommonPacksPage_marketRoot } from './__generated__/index.graphql';
import { CardPackGroup } from '../BuyPacks/CardPackGroup';
import { CardPackGroupWrapper } from '../BuyPacks/CardPackGroup/CardPackGroupWrapper';
import { CommonPacksHeader } from '../BuyPacks/CommonPacksHeader';
import { CompactDailyDeals } from '../BuyPacks/CompactDailyDeals';
import { DailyDealsTitle } from '../BuyPacks/DailyDeals/DailyDealsTitle';
import { PackOpeningDialog } from '../BuyPacks/PackOpeningDialog';
import { usePackOpeningDialog } from '../BuyPacks/PackOpeningDialog/usePackOpeningDialog';
import { SpecialEventCardPackGroup } from '../BuyPacks/SpecialEventCardPackGroup';
import { SpecialEventPromoTiles } from '../BuyPacks/SpecialEventPromoTiles';
import { usePackAssets } from '../PacksTypePage/usePackAssets';
import winterLogo from './assets/winter26.svg';
import winterBackground from './assets/winterBackground.webp';

const SpanTwoColumnsWrapper = styled.div`
  grid-column-start: 2;
  grid-column-end: 4;
`;
const WinterLogo = styled.img`
  height: calc(5 * var(--unit));
`;

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { cardPackGroups, gemsPacks } = marketRoot;
  const { openedCardPack, onPackSelect, onPackOpeningDialogClose } =
    usePackOpeningDialog(cardPackGroups);

  const {
    dailyDealCardPackGroups,
    specialEventCardPackGroups,
    marketCardPackGroups,
  } = groupBy(marketRoot.cardPackGroups, cardPackGroup => {
    if (cardPackGroup.packConfigGroupType === PackConfigGroup.DAILY_DEAL) {
      return 'dailyDealCardPackGroups';
    }
    if (cardPackGroup.packConfigGroupType === PackConfigGroup.SPECIAL_EVENT) {
      return 'specialEventCardPackGroups';
    }
    return 'marketCardPackGroups';
  });
  const dailyDealCardPackGroup = dailyDealCardPackGroups?.at(0);
  const specialEventCardPackGroup = specialEventCardPackGroups?.at(0);

  const { bgImage } = usePackAssets({
    cardPackGroupId: dailyDealCardPackGroup?.id || '',
  });

  return (
    <>
      <PullToRefetch refetch={refetch}>
        <Vertical gap={3}>
          <CommonPacksHeader
            marketRoot={marketRoot}
            refetch={refetch}
            backgroundUrl={
              specialEventCardPackGroup ? winterBackground : bgImage
            }
            title={
              specialEventCardPackGroup ? (
                <WinterLogo src={winterLogo} alt="WNTR 2026" />
              ) : (
                <DailyDealsTitle cardPackGroup={dailyDealCardPackGroup} />
              )
            }
            balances={
              <Horizontal>
                <EssenceBalance
                  value={currentUser?.commonCardPoints || 0}
                  rarity={Rarity.common}
                />
                <GemsBalance
                  balance={currentUser?.gemsBalances[0]?.amount || 0}
                />
              </Horizontal>
            }
            isSpecialEvent={!!specialEventCardPackGroup}
          >
            {!!dailyDealCardPackGroup && !specialEventCardPackGroup && (
              <DailyDeals
                cardPackGroup={dailyDealCardPackGroup}
                onClickBuy={onPackSelect}
              />
            )}
            {!!specialEventCardPackGroup && <SpecialEventPromoTiles />}
          </CommonPacksHeader>

          <Container maxWidth={1500}>
            <Vertical gap={3}>
              {!!specialEventCardPackGroup && (
                <CardPackGroupWrapper className="reverseDirection">
                  {!!dailyDealCardPackGroup && (
                    <CompactDailyDeals
                      cardPackGroup={dailyDealCardPackGroup}
                      onClickBuy={onPackSelect}
                    />
                  )}
                  <SpanTwoColumnsWrapper>
                    <SpecialEventCardPackGroup
                      cardPackGroup={specialEventCardPackGroup}
                      onClickBuy={onPackSelect}
                    />
                  </SpanTwoColumnsWrapper>
                </CardPackGroupWrapper>
              )}
              {marketCardPackGroups?.map(group => (
                <CardPackGroup
                  key={group.id}
                  cardPackGroup={group}
                  onClickBuy={onPackSelect}
                />
              ))}
            </Vertical>
          </Container>
        </Vertical>
      </PullToRefetch>
      {!!openedCardPack && (
        <PackOpeningDialog
          open
          onClose={onPackOpeningDialogClose}
          cardPack={openedCardPack}
          gemsPacks={gemsPacks}
          showPlayerDetails
          starCardPackSlug={
            cardPackGroups
              .find(group =>
                group.cardPacks.some(pack => pack.slug === openedCardPack.slug)
              )
              ?.cardPacks.find(pack => !!pack.rewardedByTask)?.slug
          }
        />
      )}
    </>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      cardPackGroups(sport: $sport) {
        id
        packConfigGroupType
        cardPacks {
          slug
          ...PackOpeningDialog_cardPack
        }
        ...CardPackGroup_cardPackGroup
        ...usePackOpeningDialog_cardPackGroup
        ...DailyDeals_cardPackGroup
        ...CompactDailyDeals_cardPackGroup
        ...SpecialEventCardPackGroup_cardPackGroup
      }
      gemsPacks: inGameCurrencyPacks(sport: $sport, currency: COMMON_GEM) {
        id
        ...PackOpeningDialog_inGameCurrencyPack
      }
      ...CommonPacksHeader_marketRoot
    }
    ${CommonPacksHeader.fragments.marketRoot}
    ${DailyDeals.fragments.cardPackGroup}
    ${CompactDailyDeals.fragments.cardPackGroup}
    ${usePackOpeningDialog.fragments.cardPackGroup}
    ${CardPackGroup.fragments.cardPackGroup}
    ${SpecialEventCardPackGroup.fragments.cardPackGroup}
    ${PackOpeningDialog.fragments.cardPack}
    ${PackOpeningDialog.fragments.inGameCurrencyPack}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
};
